import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { ApiError, watchSession } from './api/client';
import type { PaymentOption, PublicSession } from './api/types';
import { formatFiat, optKey } from './utils/format';
import { chainLabel, networkMode, setSessionNetwork } from './chains';
import { isEmbedded, onHostCurrency, post, startResizeReporting } from './embed-bridge';
import { hasExplicitLanguage, setLocale, t } from './i18n';
import { useI18n } from './i18n/use-i18n';
import { availableCurrencies, detectDisplayCurrency } from './currency';
import { applyTheme } from './main';
import { Header } from './components/Header';
import { AssetPicker } from './components/AssetPicker';
import { PaymentPanel } from './components/PaymentPanel';
import { WalletButtons } from './components/WalletButtons';
import { StatusBanner } from './components/StatusBanner';
import { Footer } from './components/Footer';
import { startDiscovery } from './wallets';

function sessionIdFromUrl(): string | null {
  const p = new URLSearchParams(location.search);
  const fromQuery = p.get('session') ?? p.get('s');
  if (fromQuery) return fromQuery;
  // /c/{id} or /{id}
  const m = location.pathname.match(/^\/(?:c\/)?([A-Za-z0-9_-]{6,})\/?$/);
  return m ? m[1] : null;
}

// Terminal states where the pay panel is hidden. `underpaid` stays interactive so the payer can
// send the remainder to the same address.
const TERMINAL = new Set(['paid', 'overpaid', 'expired', 'cancelled']);

export function App() {
  const sessionId = useMemo(sessionIdFromUrl, []);
  const [session, setSession] = useState<PublicSession | null>(null);
  const [error, setError] = useState<'notfound' | 'network' | null>(null);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [picking, setPicking] = useState(true);
  const [displayCurrency, setDisplayCurrency] = useState<string | null>(null);
  const lastStatus = useRef<string | null>(null);
  useI18n();

  useEffect(() => {
    startDiscovery();
  }, []);

  useEffect(() => {
    if (!sessionId) return;
    const w = watchSession(
      sessionId,
      (s) => {
        setSessionNetwork(s.livemode);
        setSession(s);
        setError(null);
        if (!selectedKey && s.payment_options.length === 1) {
          setSelectedKey(optKey(s.payment_options[0]));
          setPicking(false);
        }
        if (lastStatus.current !== s.status) {
          lastStatus.current = s.status;
          post({ source: 'coinvello', type: 'status', sessionId, status: s.status, session: s });
          if (s.status === 'paid' || s.status === 'overpaid') post({ source: 'coinvello', type: 'paid', sessionId, session: s });
          if (s.status === 'expired') post({ source: 'coinvello', type: 'expired', sessionId, session: s });
        }
      },
      (e) => {
        if (e instanceof ApiError && e.status === 404) setError('notfound');
        else setError('network');
      },
    );
    startResizeReporting(sessionId);
    post({ source: 'coinvello', type: 'ready', sessionId });
    return () => w.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  // Merchant branding / locale from the session (URL params and the payer's stored choice win).
  useEffect(() => {
    if (!session) return;
    const params = new URLSearchParams(location.search);
    const theme = session.branding?.theme;
    if (theme && theme !== 'auto' && !params.get('theme') && !document.documentElement.getAttribute('data-theme')) {
      applyTheme(theme);
    }
    // Merchant language hint (`locale_hints.merchant_locale`, legacy `locale`): only when the
    // payer has not chosen a language themselves (URL or stored choice).
    const hint = session.locale_hints?.merchant_locale ?? session.locale;
    if (hint && !hasExplicitLanguage()) void setLocale(hint);
  }, [session?.id]);

  // Display currency: `?currency=`, stored choice, browser locale, merchant's
  // `locale_hints.suggested_currency`, then the session currency. Hidden without `display_rates`.
  useEffect(() => {
    if (!session) return;
    const available = availableCurrencies(session.currency, session.display_rates);
    setDisplayCurrency((cur) =>
      cur && available.includes(cur)
        ? cur
        : detectDisplayCurrency(session.currency, available, undefined, session.locale_hints?.suggested_currency),
    );
  }, [session?.id, session?.currency, session?.display_rates]);

  // Host page (embed SDK) may switch the display currency at runtime.
  useEffect(
    () =>
      onHostCurrency((c) => {
        if (!session) return;
        if (availableCurrencies(session.currency, session.display_rates).includes(c)) setDisplayCurrency(c);
      }),
    [session?.id, session?.display_rates],
  );

  // Auto-redirect to success_url once `redirect_on` is reached (hosted mode only;
  // embedded hosts handle onPaid themselves).
  useEffect(() => {
    if (!session || isEmbedded || !session.success_url) return;
    const redirectOn = session.redirect_on ?? 'paid';
    const reached =
      session.status === 'paid' ||
      session.status === 'overpaid' ||
      (redirectOn === 'confirming' && session.status === 'confirming');
    if (reached) {
      const tmr = setTimeout(() => {
        location.assign(session.success_url!);
      }, 4000);
      return () => clearTimeout(tmr);
    }
  }, [session?.status, session?.success_url, session?.redirect_on]);

  const selected: PaymentOption | undefined = session?.payment_options.find((o) => optKey(o) === selectedKey);

  if (!sessionId) return <Shell><Center>{t('app.notFound')}</Center></Shell>;
  if (error === 'notfound' && !session) return <Shell><Center>{t('app.notFound')}</Center></Shell>;
  if (!session) {
    return (
      <Shell>
        <Center>
          <div class="cv-spinner" aria-hidden="true" />
          {error === 'network' ? t('app.networkError') : t('app.loading')}
        </Center>
      </Shell>
    );
  }

  const terminal = TERMINAL.has(session.status);
  const showPay = !terminal && session.status !== 'confirming' && selected && !picking;

  return (
    <Shell>
      <Header
        session={session}
        displayCurrency={displayCurrency ?? session.currency.toUpperCase()}
        onDisplayCurrency={setDisplayCurrency}
      />
      <div class="cv-body">
        {error === 'network' && <p class="cv-hint warn" role="status">{t('app.networkError')}</p>}

        {session.status !== 'pending' && <StatusBanner session={session} option={selected} />}

        {!terminal && session.status !== 'confirming' && (picking || !selected) && (
          <AssetPicker
            session={session}
            selectedKey={selectedKey}
            onSelect={(o) => {
              setSelectedKey(optKey(o));
              setPicking(false);
            }}
          />
        )}

        {showPay && selected && (
          <>
            <div class="cv-selected-summary">
              <div>
                <strong>
                  {selected.symbol} · {t('picker.network')}: {labelFor(selected)}
                </strong>
                <div class="meta">{t('picker.rate', { asset: selected.symbol, rate: formatFiat(selected.rate, session.currency) })}</div>
              </div>
              {session.payment_options.length > 1 && (
                <button class="cv-link-btn" type="button" onClick={() => setPicking(true)}>
                  {t('picker.change')}
                </button>
              )}
            </div>
            <PaymentPanel session={session} option={selected} />
            <WalletButtons session={session} option={selected} />
          </>
        )}

        {session.status === 'pending' && !picking && selected && (
          <StatusBanner session={session} option={selected} />
        )}

        <details class="cv-security">
          <summary>{t('security.title')}</summary>
          <p>{t('security.nonCustodial')}</p>
          <p>{t('security.verifyUrl')}</p>
        </details>
      </div>
      <Footer session={session} />
    </Shell>
  );
}

function labelFor(o: PaymentOption): string {
  return chainLabel(o.chain);
}


function Shell({ children }: { children: preact.ComponentChildren }) {
  return (
    <main class="cv-shell">
      {!isEmbedded && (
        <a class="cv-brand" href="https://coinvello.com" rel="noopener" aria-label={t('app.brand')}>
          <svg viewBox="0 0 100 100" width="28" height="28" aria-hidden="true">
            <rect class="mark-tile" x="6" y="6" width="88" height="88" rx="24" />
            <path class="mark-c" d="M68.6,27.8 A29,29 0 1 0 68.6,72.2" fill="none" stroke-width="8" stroke-linecap="round" />
            <polyline class="mark-v" points="38,40 50,60 62,40" fill="none" stroke-width="9" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          <span class="wordmark" aria-hidden="true">
            Coin<b>Vello</b>
          </span>
          {networkMode() === 'testnet' && <span class="cv-badge warn">{t('app.testnetBadge')}</span>}
        </a>
      )}
      <section class="cv-card" aria-label={t('app.title')}>
        {children}
      </section>
      {!isEmbedded && <p class="cv-tagline">{t('app.tagline')}</p>}
    </main>
  );
}

function Center({ children }: { children: preact.ComponentChildren }) {
  return (
    <div class="cv-center" role="status">
      {children}
    </div>
  );
}
